import * as THREE from "three";
import {BackgroundCubemap} from "./background/backgroundCubemap.js";
import {Planet} from "./planet/planet.js";
import {PlanetSurfaceGas} from "./planet/surface/planetSurfaceGas.js";
import {Lights} from "./lights.js";
import {Random} from "../math/random.js";
import {Designed} from "../design/designed.js";
import {PlanetSurfaceTexture} from "./planet/surface/planetSurfaceTexture.js";

/**
 * Environment map lighting
 */
export class Environment extends Designed {
    /**
     * Construct an environment map
     * @param {WebGLRenderer} renderer The renderer
     * @param {Scene} scene The scene
     * @param {Scene} sceneBackground The background scene
     */
    constructor(renderer, scene, sceneBackground) {
        super();

        this.renderer = renderer;
        this.scene = scene;
        this.sceneBackground = sceneBackground;
        this.lights = new Lights(renderer, scene, sceneBackground);
        this.planetSurface = null;
        this.planetPosition = new THREE.Vector3();
        this.planet = null;

        this.planetLighting = 1;
        this.planetModel = null;
        this.planetTexture = null;
        this.planetSeed = -1;
        this.planetRadius = -1;
        this.planetDistance = -1;
        this.planetAngle = -1;
        this.planetPitch = -1;
        this.planetAtmosphere = -1;
        this.planetRotation = 0;
        this.planetTilt = .17;
        this.planetColor1 = 0;
        this.planetColor2 = 0;
        this.planetColor3 = 0;
        this.planetColor4 = 0;
        this.planetColor5 = 0;
        this.planetColor6 = 0;

        this.cubemapLeft = null;
        this.cubemapRight = null;
        this.cubemapTop = null;
        this.cubemapBottom = null;
        this.cubemapFront = null;
        this.cubemapBack = null;
        this.rotationSpeed = 0.00005;
    }

    /**
     * Unload the current design
     */
    unload() {
        super.unload();
        this.scene.remove(this.planet.lightScene);
        this.sceneBackground.remove(this.planet.scene);
        this.sceneBackground.background = null;

        this.scene.environment = null;
        this.asteroidsAsset1 = this.asteroidsAsset2 = this.asteroidsAsset3 = null;
    }

    /**
     * Load an environment
     * @param {Object} data Environment data
     */
    load(data) {
        super.load(data);

        if (data.hasOwnProperty("planet")) {
            if (data["planet"].hasOwnProperty("lighting") &&
                data["planet"].hasOwnProperty("model") &&
                data["planet"].hasOwnProperty("texture") &&
                data["planet"].hasOwnProperty("seed") &&
                data["planet"].hasOwnProperty("distance") &&
                data["planet"].hasOwnProperty("radius") &&
                data["planet"].hasOwnProperty("angle") &&
                data["planet"].hasOwnProperty("pitch") &&
                data["planet"].hasOwnProperty("atmosphere") &&
                data["planet"].hasOwnProperty("rotation") &&
                data["planet"].hasOwnProperty("tilt") &&
                data["planet"].hasOwnProperty("color1") &&
                data["planet"].hasOwnProperty("color2") &&
                data["planet"].hasOwnProperty("color3") &&
                data["planet"].hasOwnProperty("color4") &&
                data["planet"].hasOwnProperty("color5") &&
                data["planet"].hasOwnProperty("color6")) {
                this.planetLighting = data["planet"]["lighting"];
                this.planetModel = data["planet"]["model"];
                this.planetTexture = data["planet"]["texture"];
                this.planetSeed = data["planet"]["seed"];
                this.planetDistance = data["planet"]["distance"];
                this.planetRadius = data["planet"]["radius"];
                this.planetAngle = data["planet"]["angle"];
                this.planetPitch = data["planet"]["pitch"];
                this.planetAtmosphere = data["planet"]["atmosphere"];
                this.planetRotation = data["planet"]["rotation"];
                this.planetTilt = data["planet"]["tilt"];
                this.planetColor1 = data["planet"]["color1"];
                this.planetColor2 = data["planet"]["color2"];
                this.planetColor3 = data["planet"]["color3"];
                this.planetColor4 = data["planet"]["color4"];
                this.planetColor5 = data["planet"]["color5"];
                this.planetColor6 = data["planet"]["color6"];

                const planetRandom = new Random(this.planetSeed);

                this.lights.setBackgroundStrength(this.planetLighting);

                this.planetPosition.set(
                    Math.cos(this.planetAngle) * Math.cos(this.planetPitch) * this.planetDistance,
                    Math.sin(this.planetPitch) * this.planetDistance,
                    Math.sin(this.planetAngle) * Math.cos(this.planetPitch) * this.planetDistance);

                if (this.planetModel) {
                    this.planetSurface = null;
                    this.planet = new Planet(
                        this.planetPosition,
                        this.planetRadius,
                        this.planetSurface,
                        this.planetModel,
                        planetRandom,
                        this.planetRotation,
                        this.planetTilt,
                        this.planetAtmosphere);

                    this.sceneBackground.add(this.planet.scene);
                }
                else {
                    if (this.planetTexture)
                        this.planetSurface = new PlanetSurfaceTexture(this.planetTexture);
                    else
                        this.planetSurface = new PlanetSurfaceGas(
                            planetRandom,
                            Number.parseInt(this.planetColor1, 16),
                            Number.parseInt(this.planetColor2, 16),
                            Number.parseInt(this.planetColor3, 16),
                            Number.parseInt(this.planetColor4, 16),
                            Number.parseInt(this.planetColor5, 16),
                            Number.parseInt(this.planetColor6, 16));

                    this.planetSurface.load(this.renderer).then(() => {
                        this.planet = new Planet(
                            this.planetPosition,
                            this.planetRadius,
                            this.planetSurface,
                            null,
                            planetRandom,
                            this.planetRotation,
                            this.planetTilt,
                            this.planetAtmosphere);

                        this.scene.add(this.planet.lightScene);
                        this.sceneBackground.add(this.planet.scene);
                    });
                }
            }
        }

        if (data.hasOwnProperty("cubemap")) {
            if (data["cubemap"].hasOwnProperty("left") &&
                data["cubemap"].hasOwnProperty("right") &&
                data["cubemap"].hasOwnProperty("top") &&
                data["cubemap"].hasOwnProperty("bottom") &&
                data["cubemap"].hasOwnProperty("front") &&
                data["cubemap"].hasOwnProperty("back")) {
                this.cubemapLeft = data["cubemap"]["left"];
                this.cubemapRight = data["cubemap"]["right"];
                this.cubemapTop = data["cubemap"]["top"];
                this.cubemapBottom = data["cubemap"]["bottom"];
                this.cubemapFront = data["cubemap"]["front"];
                this.cubemapBack = data["cubemap"]["back"];

                const background = new BackgroundCubemap(
                    this.cubemapLeft,
                    this.cubemapRight,
                    this.cubemapTop,
                    this.cubemapBottom,
                    this.cubemapFront,
                    this.cubemapBack);

                background.generate(this.renderer).then(() => {
                    this.scene.environment = this.sceneBackground.background = background.texture;
                });
            }
        }
    }

    /**
     * Get the design in JSON format
     * @returns {Object} The design in JSON format
     */
    getDesign() {
        const design = {
            "seed": this.seed
        };

        if (this.planet)
            design["planet"] = {
                "lighting": this.planetLighting,
                "model": this.planetModel,
                "texture": this.planetTexture,
                "seed": this.planetSeed,
                "distance": this.planetDistance,
                "radius": this.planetRadius,
                "angle": this.planetAngle,
                "pitch": this.planetPitch,
                "atmosphere": this.planetAtmosphere,
                "rotation": this.planetRotation,
                "tilt": this.planetTilt,
                "color1": this.planetColor1.toString(16),
                "color2": this.planetColor2.toString(16),
                "color3": this.planetColor3.toString(16),
                "color4": this.planetColor4.toString(16),
                "color5": this.planetColor5.toString(16),
                "color6": this.planetColor6.toString(16)
            };

        if (this.cubemapLeft &&
            this.cubemapRight &&
            this.cubemapTop &&
            this.cubemapBottom &&
            this.cubemapFront &&
            this.cubemapBack)
            design["cubemap"] = {
                "left": this.cubemapLeft,
                "right": this.cubemapRight,
                "top": this.cubemapTop,
                "bottom": this.cubemapBottom,
                "front": this.cubemapFront,
                "back": this.cubemapBack
            };

        return design;
    }

    /**
     * Update
     * @param {number} delta The time delta
     */
    update(delta) {
        this.planetSurface?.update(delta);
        this.planet?.update();
    }

    /**
     * Update the environment before rendering
     * @param {number} time The time interpolation in the range [0, 1]
     */
    render(time) {
        this.planetSurface?.render(this.renderer, time);
        this.planet?.render(time);
    }
}